import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  InternalReportStatusEnum,
  ReportStatus,
} from "../api/models/report.models";
import { Report } from "../api/models/report.models";
import useAdminReportCommands from "../hooks/useAdminReportCommands";
import moment from "moment";
import { useAdminDataContext } from "../contexts/adminDataContext";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.div`
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.9rem;
`;

const CommentBox = styled.div`
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e1e1e1;
  min-height: 80px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const StatusBadge = styled.div<{ $status?: InternalReportStatusEnum }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

interface AdminReportReviewDialogProps {
  visible: boolean;
  onHide: () => void;
  report: Report;
}

const AdminReportReviewDialog: React.FC<AdminReportReviewDialogProps> = ({
  visible,
  onHide,
  report,
}) => {
  const { t } = useTranslation();
  const { users, isLoadingUsers } = useAdminDataContext();

  const {
    updateReportStatus,
    updateInternalStatus,
    isUpdatingReportStatus,
    isUpdatingInternalReportStatus,
  } = useAdminReportCommands();

  const getStatusText = (status?: InternalReportStatusEnum) => {
    switch (status) {
      case InternalReportStatusEnum.ToBeReviewed:
        return t("label-to-be-reviewed");
      case InternalReportStatusEnum.ReturnedForCorrection:
        return t("label-returned-for-correction");
      case InternalReportStatusEnum.ApprovedAutomatically:
        return t("label-approved-automatically");
      case InternalReportStatusEnum.ApprovedManually:
        return t("label-approved-manually");
      default:
        return t("label-no-status");
    }
  };

  const handleApprove = async () => {
    if (!report) return;

    const result = await updateInternalStatus({
      id: report.id,
      internalStatus: InternalReportStatusEnum.ApprovedManually,
    });

    if (result) {
      onHide();
    }
  };

  const handleReturnForCorrection = async () => {
    if (!report) return;

    const result = await updateReportStatus({
      id: report.id,
      status: ReportStatus.Draft,
    });

    if (result) {
      onHide();
    }
  };

  const approvedByUser = report.approvedBy
    ? users?.find((user) => user.id === report.approvedBy)
    : undefined;

  return (
    <Dialog
      header={t("header-review-report")}
      visible={visible}
      onHide={onHide}
      style={{ width: "600px" }}
      modal
    >
      <DialogContent>
        <LoadingContainer
          status={
            isLoadingUsers
              ? HttpRequestStatus.LOADING
              : HttpRequestStatus.SUCCESS
          }
        >
          <Section>
            <Label>{t("label-current-status")}</Label>
            <StatusBadge $status={report.internalReportStatus}>
              {getStatusText(report.internalReportStatus)}
            </StatusBadge>
          </Section>

          {report.approvedAt && approvedByUser && (
            <Section>
              <Label>{t("label-approved-by")}</Label>
              <StatusBadge>
                {approvedByUser?.names?.firstName +
                  " " +
                  approvedByUser?.names?.lastName +
                  " " +
                  moment(report.approvedAt).format("DD.MM.YYYY HH:mm")}
              </StatusBadge>
            </Section>
          )}

          <Section>
            <Label>{t("label-customer-comment")}</Label>
            <CommentBox>
              {report.comment || t("text-no-comment-available")}
            </CommentBox>
          </Section>

          <ButtonContainer>
            <Button
              label={t("cmd-cancel")}
              onClick={onHide}
              outlined
              style={{ marginRight: "auto" }}
              disabled={
                isUpdatingReportStatus || isUpdatingInternalReportStatus
              }
            />
            <Button
              label={t("cmd-return-for-correction")}
              severity="warning"
              onClick={handleReturnForCorrection}
              icon="pi pi-reply"
              loading={isUpdatingReportStatus || isUpdatingInternalReportStatus}
              disabled={
                report.internalReportStatus ===
                InternalReportStatusEnum.ApprovedManually
              }
            />
            <Button
              label={t("cmd-approve")}
              severity="success"
              onClick={handleApprove}
              icon="pi pi-check"
              loading={isUpdatingReportStatus || isUpdatingInternalReportStatus}
              disabled={
                report.internalReportStatus ===
                InternalReportStatusEnum.ApprovedManually
              }
            />
          </ButtonContainer>
        </LoadingContainer>
      </DialogContent>
    </Dialog>
  );
};

export default AdminReportReviewDialog;
